'use client'

import { useEffect, useRef, isValidElement, memo } from 'react'
import ReactMarkdown, { type Components } from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { TrendingUp, Search, Globe } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { ToolChecklist, type ToolCall } from './ToolChecklist'
import { VisualBlock, isVisualBlockLanguage } from './VisualBlocks'
import Logo from '@/components/ui/Logo'
import { isRtl } from '@/lib/utils/rtl'

interface Message {
  _id: string
  role: 'user' | 'assistant'
  content: string
  status: 'streaming' | 'complete' | 'failed'
  errorMessage?: string
  toolCalls?: ToolCall[]
  createdAt: number
}

interface MessageListProps {
  messages: Message[]
  onStarterPrompt?: (prompt: string) => void
  onRetry?: (messageId: string) => void
}

// Keys are relative to the `assistant.messageList` namespace used by
// useTranslations below — do NOT prefix them with `messageList.` again.
const STARTER_PROMPT_CONFIGS = [
  {
    textKey: 'starterPrompt1' as const,
    icon: <TrendingUp style={{ width: '14px', height: '14px' }} />,
    iconBg: 'var(--color-lime-muted)',
    iconColor: 'var(--color-lime)',
  },
  {
    textKey: 'starterPrompt2' as const,
    icon: <Search style={{ width: '14px', height: '14px' }} />,
    iconBg: 'var(--color-cyan-muted)',
    iconColor: 'var(--color-cyan)',
  },
  {
    textKey: 'starterPrompt3' as const,
    icon: <Globe style={{ width: '14px', height: '14px' }} />,
    iconBg: 'var(--color-purple-muted)',
    iconColor: 'var(--color-purple)',
  },
]

/** Extracts a `42a:*` visual-block language from a markdown code className. */
function visualLanguageOf(className: string | undefined): string | null {
  const match = /language-(42a:[a-z]+)/.exec(className ?? '')
  return match ? match[1] : null
}

/**
 * ReactMarkdown component overrides that route ```42a:metric / 42a:bar /
 * 42a:trend fenced blocks to <VisualBlock>.
 *
 * IMPORTANT: only the two module-level instances below may exist. Component
 * identity must be stable across renders — fresh function identities would
 * make ReactMarkdown treat them as new element types and REMOUNT every
 * visual block (charts re-animate) on each parent render.
 */
/**
 * True if a string is an internal id we must never display: a public prefixed
 * id (brn_/prj_/prm_/tpc_/cit_/tpg_ + token) or a bare 32-char Convex doc id.
 * Used to scrub ids the model may leak into a code span.
 */
function looksLikeId(s: string): boolean {
  return /^(brn|prj|prm|tpc|cit|tpg)_[A-Za-z0-9]{6,}$/.test(s) || /^[a-z0-9]{32}$/.test(s)
}

function markdownComponents(isStreaming: boolean): Components {
  return {
    code({ className, children, ...props }) {
      const raw = (Array.isArray(children) ? children.join('') : String(children ?? '')).trim()
      const lang = visualLanguageOf(className)
      if (lang && isVisualBlockLanguage(lang)) {
        return <VisualBlock language={lang} raw={raw} isStreaming={isStreaming} />
      }
      // Safety net: never render an internal id, even if the model slips one
      // into a code span. The system prompt forbids it; this guarantees it.
      if (looksLikeId(raw)) {
        return <span style={{ color: 'var(--color-text-muted)' }}>…</span>
      }
      return (
        <code className={className} {...props}>
          {children}
        </code>
      )
    },
    pre({ children, ...props }) {
      // Unwrap the <pre> around visual blocks so the markdown pre styling
      // (border/background/padding) doesn't box the rendered chart.
      const child = Array.isArray(children) ? children[0] : children
      if (isValidElement(child)) {
        const childClass = (child.props as { className?: string }).className
        if (visualLanguageOf(childClass)) {
          return <>{children}</>
        }
      }
      return <pre {...props}>{children}</pre>
    },
  }
}

// The ONLY two instances — stable identities (see markdownComponents docs).
const MARKDOWN_COMPONENTS_STREAMING = markdownComponents(true)
const MARKDOWN_COMPONENTS_STATIC = markdownComponents(false)

// Stable plugin array — a fresh array each render makes ReactMarkdown
// reconfigure its processor on every pass.
const REMARK_PLUGINS = [remarkGfm]

/**
 * Live "Thinking" line shown while an assistant message is streaming and no
 * tool is currently running (between tool calls / while composing text).
 * While a tool runs, the ToolChecklist row's spinner is the live indicator,
 * so this line hides to avoid two competing loaders. Pinned at the bottom of
 * the message and vanishes the moment the message finalizes.
 */
function ActivityLine({ toolCalls, hasContent }: { toolCalls?: ToolCall[]; hasContent: boolean }) {
  const t = useTranslations('assistant.messageList')
  if (toolCalls?.some((tc) => tc.status === 'running')) return null
  const label = t('thinking')

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.4rem',
        marginTop: hasContent ? '0.5rem' : 0,
        fontSize: '0.75rem',
        color: 'var(--color-text-muted)',
      }}
    >
      <span style={{ display: 'inline-flex', gap: '2px', alignItems: 'center' }}>
        <span className="w-1 h-1 rounded-full bg-current animate-bounce [animation-delay:0ms]" />
        <span className="w-1 h-1 rounded-full bg-current animate-bounce [animation-delay:150ms]" />
        <span className="w-1 h-1 rounded-full bg-current animate-bounce [animation-delay:300ms]" />
      </span>
      <span className="assistant-activity-label">{label}</span>
    </div>
  )
}

/** Cheap change signature for toolCalls (Convex returns fresh array identities). */
function toolCallsSignature(toolCalls?: ToolCall[]): string {
  if (!toolCalls || toolCalls.length === 0) return ''
  return toolCalls.map((t) => `${t.name}:${t.status}`).join('|')
}

/**
 * One rendered message. Memoized with a content-based comparator because
 * Convex returns brand-new message objects on every query update — identity
 * comparison would re-render (and re-parse markdown for) every historical
 * message on each streaming flush of the newest one.
 */
const MessageItem = memo(
  function MessageItem({
    msg,
    onRetry,
  }: {
    msg: Message
    onRetry?: (messageId: string) => void
  }) {
    const t = useTranslations('assistant.messageList')

    if (msg.role === 'user') {
      // User bubble: right-aligned, lime-muted bg, corner bottom-right is 0.25rem
      return (
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <div
            style={{
              maxWidth: '82%',
              background: 'var(--color-lime-muted)',
              color: 'var(--color-text-primary)',
              borderRadius: '0.75rem 0.75rem 0.25rem 0.75rem',
              padding: '0.55rem 0.875rem',
              fontSize: '0.85rem',
            }}
          >
            {/* dir=auto + unicode-bidi:plaintext: each line resolves its own
                base direction from its first strong character, so a Hebrew
                message renders right-to-left while embedded Latin words stay
                a single left-to-right run inside it (and vice versa). */}
            <p
              dir="auto"
              style={{ margin: 0, whiteSpace: 'pre-wrap', unicodeBidi: 'plaintext', textAlign: 'start' }}
            >
              {msg.content}
            </p>
          </div>
        </div>
      )
    }

    // Assistant card: left-aligned, metric-card motif with left gradient bar
    return (
      <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
        <div
          style={{
            maxWidth: '92%',
            position: 'relative',
            background: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
            borderRadius: '0.25rem 0.75rem 0.75rem 0.75rem',
            padding: '0.65rem 0.9rem 0.65rem 1.1rem',
            fontSize: '0.85rem',
            color: 'var(--color-text-primary)',
            boxShadow: 'var(--shadow-sm)',
            overflow: 'hidden',
          }}
        >
          {/* 4px lime→cyan gradient left bar */}
          <span
            aria-hidden="true"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '4px',
              height: '100%',
              background: 'var(--gradient-lime-cyan)',
              borderRadius: '0.25rem 0 0 0.75rem',
            }}
          />

          {/* Checklist of tool calls: spinner while running, check when done.
              Persists after the message completes as a record of the work. */}
          <ToolChecklist toolCalls={msg.toolCalls} />

          {msg.status === 'failed' ? (
            <div>
              <p
                title={msg.errorMessage ?? undefined}
                style={{ margin: 0, fontSize: '0.8rem', fontStyle: 'italic', color: 'var(--color-pink)' }}
              >
                {t('errorMessage')}
              </p>
              {onRetry && (
                <button
                  type="button"
                  onClick={() => onRetry(msg._id)}
                  style={{
                    marginTop: '0.5rem',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.35rem',
                    padding: '0.3rem 0.75rem',
                    borderRadius: '9999px',
                    border: '1px solid var(--color-lime)',
                    background: 'var(--color-lime-muted)',
                    color: 'var(--color-lime)',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'background 150ms',
                  }}
                >
                  {t('tryAgain')}
                </button>
              )}
            </div>
          ) : (
            <>
              {/* Base direction follows the message content (first RTL char
                  wins), so Hebrew/Arabic answers get right-aligned text, RTL
                  list bullets and logical padding. Per-block unicode-bidi:
                  plaintext (see CSS below) lets individual paragraphs that
                  start in the other script resolve independently. */}
              <div className="assistant-message-markdown" dir={isRtl(msg.content) ? 'rtl' : 'ltr'}>
                <ReactMarkdown
                  remarkPlugins={REMARK_PLUGINS}
                  components={
                    msg.status === 'streaming'
                      ? MARKDOWN_COMPONENTS_STREAMING
                      : MARKDOWN_COMPONENTS_STATIC
                  }
                >
                  {msg.content}
                </ReactMarkdown>
              </div>
              {/* Live activity line — names the running tool (or "Thinking"),
                  so the user sees work continuing even alongside partial text.
                  Disappears the moment the message finalizes. */}
              {msg.status === 'streaming' && (
                <ActivityLine toolCalls={msg.toolCalls} hasContent={msg.content !== ''} />
              )}
            </>
          )}
        </div>
      </div>
    )
  },
  (prev, next) =>
    prev.onRetry === next.onRetry &&
    prev.msg._id === next.msg._id &&
    prev.msg.content === next.msg.content &&
    prev.msg.status === next.msg.status &&
    prev.msg.errorMessage === next.msg.errorMessage &&
    toolCallsSignature(prev.msg.toolCalls) === toolCallsSignature(next.msg.toolCalls),
)

export const MessageList = memo(function MessageList({
  messages,
  onStarterPrompt,
  onRetry,
}: MessageListProps) {
  const t = useTranslations('assistant.messageList')
  const bottomRef = useRef<HTMLDivElement>(null)

  // Auto-scroll to bottom on new messages or streaming updates.
  const lastContent = messages[messages.length - 1]?.content
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages.length, lastContent])

  if (messages.length === 0) {
    return (
      <div
        className="flex-1 overflow-y-auto flex flex-col items-center justify-center"
        style={{ padding: '1.5rem 1rem' }}
      >
        {/* Logo */}
        <Logo width={64} height={24} />

        {/* Headline */}
        <p
          style={{
            fontSize: '1.35rem',
            fontWeight: 700,
            lineHeight: 1.15,
            marginTop: '0.75rem',
            marginBottom: 0,
            textAlign: 'center',
            color: 'var(--color-text-primary)',
          }}
        >
          {t('emptyHeadline')}{' '}
          <span
            style={{
              background: 'var(--gradient-lime-cyan)',
              WebkitBackgroundClip: 'text',
              backgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            {t('emptyHeadlineAccent')}
          </span>
        </p>

        {/* Caption */}
        <p
          style={{
            fontSize: '0.8rem',
            color: 'var(--color-text-muted)',
            marginTop: '0.4rem',
            marginBottom: 0,
            textAlign: 'center',
          }}
        >
          {t('emptyCaption')}
        </p>

        {/* Starter cards */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '0.5rem',
            marginTop: '1.25rem',
            width: '100%',
          }}
        >
          {STARTER_PROMPT_CONFIGS.map((prompt) => {
            const promptText = t(prompt.textKey)
            return (
            <button
              key={prompt.textKey}
              type="button"
              onClick={() => onStarterPrompt?.(promptText)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.6rem',
                textAlign: 'left',
                border: '1px solid var(--color-border)',
                background: 'var(--color-surface)',
                borderRadius: '0.75rem',
                padding: '0.625rem 0.85rem',
                fontSize: '0.8rem',
                color: 'var(--color-text-secondary)',
                cursor: 'pointer',
                width: '100%',
                transition: 'border-color 150ms, background 150ms, color 150ms',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'var(--color-lime)'
                e.currentTarget.style.background = 'var(--color-lime-muted)'
                e.currentTarget.style.color = 'var(--color-text-primary)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'var(--color-border)'
                e.currentTarget.style.background = 'var(--color-surface)'
                e.currentTarget.style.color = 'var(--color-text-secondary)'
              }}
            >
              {/* Icon square */}
              <span
                style={{
                  width: '26px',
                  height: '26px',
                  borderRadius: '0.4rem',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  background: prompt.iconBg,
                  color: prompt.iconColor,
                }}
              >
                {prompt.icon}
              </span>
              {promptText}
            </button>
            )
          })}
        </div>
      </div>
    )
  }

  return (
    <div
      className="flex-1 overflow-y-auto"
      style={{ padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.9rem' }}
    >
      {messages.map((msg) => (
        <MessageItem key={msg._id} msg={msg} onRetry={onRetry} />
      ))}

      <div ref={bottomRef} />

      <style jsx global>{`
        .assistant-activity-label {
          animation: assistant-activity-pulse 1.4s ease-in-out infinite;
        }
        @keyframes assistant-activity-pulse {
          0%, 100% { opacity: 0.55; }
          50% { opacity: 1; }
        }
        .assistant-tool-spinner {
          width: 12px;
          height: 12px;
          border-radius: 50%;
          border: 2px solid var(--color-lime-muted);
          border-top-color: var(--color-lime);
          animation: assistant-tool-spin 0.7s linear infinite;
        }
        @keyframes assistant-tool-spin {
          to { transform: rotate(360deg); }
        }
        .assistant-message-markdown {
          line-height: 1.6;
        }
        /* Bidi: each block resolves its own base direction from its first
           strong character (unicode-bidi: plaintext), so an English term
           inside a Hebrew sentence stays one isolated LTR run instead of
           flipping the sentence, and a fully-English paragraph inside an
           RTL answer reads left-to-right. text-align: start follows the
           resolved per-paragraph direction. */
        .assistant-message-markdown p,
        .assistant-message-markdown li,
        .assistant-message-markdown h1,
        .assistant-message-markdown h2,
        .assistant-message-markdown h3,
        .assistant-message-markdown blockquote {
          unicode-bidi: plaintext;
          text-align: start;
        }
        .assistant-message-markdown p {
          margin-bottom: 0.625rem;
          color: var(--color-text-secondary);
        }
        .assistant-message-markdown p:last-child {
          margin-bottom: 0;
        }
        /* Visual blocks: hug the sentence that introduces them (small top
           margin) and clearly separate from the next section (large bottom). */
        .assistant-message-markdown .assistant-visual-block {
          margin: 0.4rem 0 1.25rem;
        }
        .assistant-message-markdown .assistant-visual-block:last-child {
          margin-bottom: 0.25rem;
        }
        .assistant-message-markdown ul,
        .assistant-message-markdown ol {
          /* Logical padding: indents from the right (with bullets on the
             right) when the message direction is RTL. */
          padding-inline-start: 1.25rem;
          margin-bottom: 0.5rem;
          color: var(--color-text-secondary);
        }
        .assistant-message-markdown li {
          margin-bottom: 0.25rem;
        }
        .assistant-message-markdown ul li {
          list-style-type: disc;
        }
        .assistant-message-markdown ol li {
          list-style-type: decimal;
        }
        .assistant-message-markdown strong {
          font-weight: 600;
          color: var(--color-text-primary);
        }
        .assistant-message-markdown code {
          background: var(--color-surface);
          border: 1px solid var(--color-border);
          border-radius: 0.25rem;
          padding: 0.125rem 0.25rem;
          font-size: 0.8em;
          color: var(--color-lime);
          /* Code is always LTR, isolated so it doesn't reorder the
             surrounding RTL sentence. */
          direction: ltr;
          unicode-bidi: isolate;
        }
        .assistant-message-markdown pre {
          background: var(--color-surface);
          border: 1px solid var(--color-border);
          border-radius: 0.5rem;
          padding: 0.75rem;
          margin-bottom: 0.5rem;
          overflow-x: auto;
          /* Code blocks stay LTR even inside an RTL answer. */
          direction: ltr;
          text-align: left;
        }
        .assistant-message-markdown pre code {
          background: transparent;
          border: none;
          padding: 0;
          color: var(--color-text-primary);
        }
        .assistant-message-markdown h1,
        .assistant-message-markdown h2,
        .assistant-message-markdown h3 {
          font-weight: 600;
          color: var(--color-text-primary);
          margin-top: 0.75rem;
          margin-bottom: 0.375rem;
        }
        .assistant-message-markdown h1 { font-size: 1.125rem; }
        .assistant-message-markdown h2 { font-size: 1rem; }
        .assistant-message-markdown h3 { font-size: 0.9375rem; }
        .assistant-message-markdown a {
          color: var(--color-lime);
          text-decoration: underline;
        }
        .assistant-message-markdown blockquote {
          border-inline-start: 3px solid var(--color-border);
          padding-inline-start: 0.75rem;
          color: var(--color-text-muted);
          font-style: italic;
          margin: 0.5rem 0;
        }
      `}</style>
    </div>
  )
})
